import { logger } from "../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { X, ZoomIn } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setImages(response.data);
      } catch (err) {
        logger.error("Error fetching gallery:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // Sample images if gallery is empty
  const sampleImages = [
    { id: "1", url: "https://images.pexels.com/photos/18341169/pexels-photo-18341169.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600", caption: "Poker chips" },
    { id: "2", url: "https://images.pexels.com/photos/2555292/pexels-photo-2555292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600", caption: "Poker action" },
    { id: "3", url: "https://images.pexels.com/photos/6532369/pexels-photo-6532369.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600", caption: "Trophy" },
  ];

  const displayImages = images.length > 0 ? images : sampleImages;

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-casino-black">
        <div className="text-gold animate-pulse font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="gallery-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-casino-black to-casino-dark relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <p className="text-gold uppercase tracking-[0.3em] text-sm mb-6 font-accent">
            Memories
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white mb-6">
            Photo <span className="gold-gradient">Gallery</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto">
            Relive the excitement and memories from San Marcos Social Club events and tournaments.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-casino-black" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-4">
          {displayImages.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/60 text-lg">No photos yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {displayImages.map((image, index) => (
                <div
                  key={image.id}
                  className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-image-${index}`}
                >
                  <img
                    src={image.url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${image.url}` : image.url}
                    alt={image.caption || "Gallery image"}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-300 flex items-center justify-center">
                    <ZoomIn className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  {image.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-white text-sm">{image.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-5xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${selectedImage.url}` : selectedImage.url}
              alt={selectedImage.caption || "Gallery image"}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            {selectedImage.caption && (
              <p className="text-white text-center mt-4">{selectedImage.caption}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
